import styled from '@emotion/styled';

import { COLORS, SERIF_FONT_FAMILY, Text, mq } from '@clutter/clean';

import { ResponsiveImage } from '@shared/responsive_image';

export const HeroSectionWrapper = styled.section`
  background: white;
  width: 100%;
`;

export const HeroSection = styled.div`
  padding: 60px 24px 80px;
  max-width: 1050px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  min-height: 80vh;

  ${mq({
    padding: ['0px 24px 60px', '60px 24px 80px', '0px 24px 0px'],
    flexDirection: ['column-reverse', 'column-reverse', 'row'],
    gap: ['40px', '50px', '40px'],
  })}
`;

export const LeftSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;

  ${mq({
    width: ['100%', '100%', '50%'],
  })}
`;

export const RightSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  ${mq({
    width: ['100%', '100%', '50%'],
  })}
`;

export const TextContent = styled.div`
  flex: 1;

  ${mq({
    textAlign: ['center', 'center', 'left'],
    width: ['327px', 'auto', 'auto'],
    maxWidth: ['327px', '100%', '474px'],
    paddingLeft: ['0', '0', '0'],
    paddingRight: ['0', '20px', '20px'],
    margin: ['0 auto', '0', '0'],
  })}
`;

export const PhotoGrid = styled.div`
  flex: 1;
  position: relative;
  min-height: 650px;
  width: 100%;
  overflow: visible;

  ${mq({
    maxWidth: ['100%', '450px', '480px'],
    minHeight: ['400px', '600px', '650px'],
    display: ['block', 'block', 'block'],
    gap: ['0', '0', '0'],
    padding: ['20px 0 40px', '0', '0'],
    marginTop: ['0', '0', '0'],
    paddingLeft: ['0', '0', '0'],
  })}
`;

export const MainHeading = styled(Text.Display)`
  margin-bottom: 24px;
  color: #000000;
  font-family: ${SERIF_FONT_FAMILY};
  font-weight: 500;
  line-height: 140%;
  letter-spacing: 0px;

  ${mq({
    fontSize: ['32px', '36px', '40px'],
    lineHeight: ['140%', '130%', '140%'],
    marginBottom: ['20px', '24px', '32px'],
  })}
`;

export const HighlightedWord = styled.span<{
  backgroundColor: string;
  textColor: string;
}>`
  background: ${(props) => props.backgroundColor};
  color: ${(props) => props.textColor};
  padding: 2px 8px;
  border-radius: 24px;
  display: inline-block;
  margin-left: 4px;
  font-weight: 500;
  transition:
    background-color 0.5s ease,
    color 0.5s ease;
  white-space: nowrap;
  width: fit-content;
  height: fit-content;

  ${mq({
    padding: ['1px 6px', '2px 8px', '2px 8px'],
    borderRadius: ['18px', '22px', '24px'],
    fontSize: ['32px', '1em', '1em'],
    marginLeft: ['2px', '4px', '4px'],
  })}
`;

export const ImageCard = styled.div<{
  position: string;
  size: string;
  isHighlighted?: boolean;
  highlightColor?: string;
}>`
  border-radius: 4px;
  overflow: hidden;
  box-shadow: ${(props) => {
    if (props.isHighlighted && props.highlightColor) {
      // Use gradient shadows for all positions when highlighted
      if (props.position === 'top-center') {
        return `0 2px 15px 2px rgba(252, 178, 44, 0.6)`;
      }
      if (props.position === 'bottom-right') {
        return `0 2px 15px 2px rgba(230, 125, 115, 0.6)`;
      }
      if (props.position === 'top-right') {
        return `0 2px 15px 2px rgba(161, 230, 224, 0.6)`;
      }
      if (props.position === 'center-large') {
        return `0 2px 15px 2px rgba(27, 144, 137, 0.6)`;
      }
      // Fallback (should not be reached)
      return `0 2px 15px 2px ${props.highlightColor}`;
    }
    // No shadow when not highlighted
    return 'none';
  }};
  border-radius: ${(props) => {
    // Smaller border radius for top-center and bottom-right when highlighted
    if (
      (props.position === 'top-center' || props.position === 'bottom-right') &&
      props.isHighlighted
    ) {
      return '2px';
    }
    return '4px';
  }};
  transition:
    transform 0.3s ease,
    box-shadow 0.5s ease,
    border-radius 0.3s ease;
  background: ${COLORS.cloud};

  /* Mobile: Absolute positioning with specific design dimensions */
  @media (max-width: 767px) {
    position: absolute;

    ${(props) => {
      const mobilePositions: Record<string, string> = {
        'top-center': `
          top: 53px;
          left: 24px;
          width: 80.1px;
          height: 74.48px;
          border-radius: 8.43px;
        `,
        'top-right': `
          top: 40px;
          left: 229px;
          width: 121.5px;
          height: 112.39px;
          border-radius: 9.11px;
        `,
        'center-large': `
          top: 135.69px;
          left: 78px;
          width: 219px;
          height: 202.73px;
          border-radius: 12.2px;
        `,
        'bottom-right': `
          top: 306px;
          left: 248px;
          width: 102.6px;
          height: 95.4px;
          border-radius: 10.8px;
        `,
      };
      return mobilePositions[props.position] || '';
    }}
  }

  /* Tablet and Desktop: Absolute positioning */
  @media (min-width: 768px) {
    position: absolute;
    width: auto;
    aspect-ratio: auto;

    ${(props) => {
      const positions: Record<string, string> = {
        'top-center': `
          top: 50px;
          left: 0%;
          transform: translateX(-50%);
          width: 114px;
          height: 106px;
          z-index: ${props.isHighlighted ? 10 : 3};
        `,
        'top-right': `
          top: 60px;
          right: 0px;
          width: 160px;
          height: 148px;
          z-index: ${props.isHighlighted ? 10 : 1};
        `,
        'center-large': `
          top: 50%;
          left: 40%;
          transform: translate(-50%, -50%);
          width: 323px;
          height: 299px;
          z-index: ${props.isHighlighted ? 10 : 2};
        `,
        'bottom-right': `
          bottom: 110px;
          right: 0px;
          width: 114px;
          height: 106px;
          z-index: ${props.isHighlighted ? 10 : 4};
        `,
      };
      return positions[props.position] || '';
    }}
  }

  /* Tablet adjustments */
  @media (min-width: 768px) and (max-width: 1023px) {
    ${(props) => {
      if (props.position === 'top-right') {
        return `
          right: 120px;
          width: 140px;
          height: 130px;
        `;
      }
      if (props.position === 'center-large') {
        return `
          width: 280px;
          height: 260px;
        `;
      }
      if (props.position === 'bottom-right') {
        return `
          right: 100px;
        `;
      }
      return '';
    }}
  }

  ${(props) =>
    props.position === 'center-large' &&
    `
    border-radius: 8px;
  `}

  &:hover {
    @media (max-width: 767px) {
      transform: scale(1.02);
    }

    @media (min-width: 768px) {
      ${(props) => {
        if (props.position === 'center-large') {
          return 'transform: translate(-50%, calc(-50% - 4px)) scale(1.02);';
        } else if (props.position === 'top-center') {
          return 'transform: translateX(calc(-50% - 2px)) translateY(-4px) scale(1.02);';
        } else {
          return 'transform: translateY(-4px) scale(1.02);';
        }
      }}
      ${(props) => {
        // Use exactly the same shadow logic as the main highlighted state
        let boxShadow = '';

        if (props.position === 'center-large') {
          // Use the same as main highlighted state
          boxShadow = `0 2px 15px 2px rgba(27, 144, 137, 0.6)`;
        } else if (props.position === 'top-center') {
          // Use the same as main highlighted state
          boxShadow = `0 2px 15px 2px rgba(252, 178, 44, 0.6)`;
        } else if (props.position === 'bottom-right') {
          boxShadow = `0 2px 15px 2px rgba(230, 125, 115, 0.6)`;
        } else if (props.position === 'top-right') {
          boxShadow = `0 2px 15px 2px rgba(161, 230, 224, 0.6)`;
        } else {
          // Fallback
          boxShadow = '0 2px 15px 2px rgba(27, 144, 137, 0.6)';
        }

        return `box-shadow: ${boxShadow};`;
      }}
      z-index: 10;
    }
  }
`;

export const StyledImage = styled(ResponsiveImage)`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;
